import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCloseOutline } from "react-icons/io5";
import Popup from "src/components/Popup/Popup";
import { useApi } from "src/hooks/useApi";

interface GoaCode {
    id: string;
    nr: string;
    section: string;
    description: string;
    points: number;
    euroAmount: number;
    notes?: string;
}

interface GoaCatalogResponse {
    codes: GoaCode[];
    specialtySections: string[];
    scope: "specialty" | "all";
}

// Section A ("Gebühren in besonderen Fällen") is intentionally omitted — it's a rule
// listing of reduced-multiplier-cap codes from other sections, not a billing-code
// section of its own, so it has no rows in GOA_CATALOG and would always filter to empty.
const SECTION_LABELS: Record<string, string> = {
    B: "Grundleistungen und allgemeine Leistungen",
    C: "Nichtgebietsbezogene Sonderleistungen",
    D: "Anästhesieleistungen",
    E: "Physikalisch-medizinische Leistungen",
    F: "Innere Medizin, Kinderheilkunde, Dermatologie",
    G: "Neurologie, Psychiatrie und Psychotherapie",
    H: "Geburtshilfe und Gynäkologie",
    I: "Augenheilkunde",
    J: "Hals-, Nasen-, Ohrenheilkunde",
    K: "Urologie",
    L: "Chirurgie, Orthopädie",
    M: "Laboratoriumsuntersuchungen",
    N: "Histologie, Zytologie und Zytogenetik",
    O: "Strahlendiagnostik, Nuklearmedizin, MRT, Strahlentherapie",
    P: "Sektionsleistungen",
};

const fmt = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Zuschlag codes carry an internal "<Section>-" disambiguation prefix (e.g. "B-A"
// for Zuschlag A) that isn't real GOÄ notation — never show it to the user.
const displayCode = (code: string) => (code || "").replace(/^[A-Z]-(?=[A-Z])/, "");

export function GoaCatalogPopup({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [scope, setScope] = useState<"specialty" | "all">("specialty");
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const searching = search.trim().length > 0;

    const url = `/goa/catalog?scope=${scope}${searching ? `&search=${encodeURIComponent(search.trim())}` : ""}`;
    const { data, loading, error } = useApi<GoaCatalogResponse>(url);
    const codes = data?.codes ?? [];

    // Chip options reflect the current scope only (not the search text), so the
    // chip bar stays stable while the user types instead of shrinking/growing.
    const availableSections = scope === "all" ? Object.keys(SECTION_LABELS) : (data?.specialtySections ?? []);

    const setScopeAndReset = (s: "specialty" | "all") => {
        setScope(s);
        setActiveSection(null);
    };

    const filteredCodes = activeSection ? codes.filter((c) => c.section === activeSection) : codes;

    // Group the already arithmetically-sorted list into consecutive section runs.
    const groups = useMemo(() => {
        const out: { section: string; rows: GoaCode[] }[] = [];
        for (const code of filteredCodes) {
            const last = out[out.length - 1];
            if (last && last.section === code.section) last.rows.push(code);
            else out.push({ section: code.section, rows: [code] });
        }
        return out;
    }, [filteredCodes]);

    return (
        <Popup
            className="popup--wide"
            title={t("invoicePage.goaCatalogTitle", { defaultValue: "GOÄ-Ziffern" })}
            onClose={onClose}
            message={
                <div className="goa-catalog-popup">
                    <div className="goa-catalog-popup__controls">
                        <div className="toggle-group">
                            <button
                                type="button"
                                className={`toggle-btn${scope === "specialty" ? " toggle-btn--active" : ""}`}
                                onClick={() => setScopeAndReset("specialty")}
                            >
                                {t("invoicePage.goaScopeSpecialty", { defaultValue: "Meine Fachrichtung" })}
                            </button>
                            <button
                                type="button"
                                className={`toggle-btn${scope === "all" ? " toggle-btn--active" : ""}`}
                                onClick={() => setScopeAndReset("all")}
                            >
                                {t("invoicePage.goaScopeAll", { defaultValue: "Alle Fachgebiete" })}
                            </button>
                        </div>
                        <div className="goa-catalog-popup__search-wrap">
                            <input
                                type="text"
                                className="goa-catalog-popup__search"
                                placeholder={t("invoicePage.goaCatalogSearchPlaceholder", { defaultValue: "Nr. oder Bezeichnung suchen…" })}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                autoFocus
                            />
                            {searching && (
                                <IoCloseOutline
                                    className="goa-catalog-popup__search-clear"
                                    onClick={() => setSearch("")}
                                    role="button"
                                    aria-label={t("form.clear", { defaultValue: "Clear" })}
                                />
                            )}
                        </div>
                    </div>
                    {searching && scope === "specialty" && (
                        <p className="goa-catalog-popup__hint">
                            {t("invoicePage.goaSearchHint", {
                                defaultValue: "Die Suche durchsucht nur Ihre Fachrichtung. Für alle Fachgebiete oben umschalten.",
                            })}
                        </p>
                    )}

                    <div className="goa-catalog-popup__section-chips">
                        <button
                            type="button"
                            className={`goa-catalog-popup__chip${activeSection === null ? " goa-catalog-popup__chip--active" : ""}`}
                            onClick={() => setActiveSection(null)}
                        >
                            {t("filters.all")}
                        </button>
                        {availableSections.map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={`goa-catalog-popup__chip${activeSection === s ? " goa-catalog-popup__chip--active" : ""}`}
                                onClick={() => setActiveSection(activeSection === s ? null : s)}
                                title={SECTION_LABELS[s] ?? s}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    <div className="goa-catalog-popup__scroll">
                        {loading ? (
                            <div className="goa-catalog-popup__list-loading">
                                <div className="loader-spinner" />
                            </div>
                        ) : error ? (
                            <p className="goa-catalog-popup__empty goa-catalog-popup__empty--error">{error}</p>
                        ) : !filteredCodes.length ? (
                            <p className="goa-catalog-popup__empty">{t("invoicePage.goaCatalogEmpty", { defaultValue: "Keine Ziffern gefunden." })}</p>
                        ) : (
                            <table className="goa-catalog-popup__table">
                                <thead>
                                    <tr>
                                        <th>{t("invoicePage.examPosition")}</th>
                                        <th>{t("invoicePage.examDescription")}</th>
                                        <th className="goa-catalog-popup__num">{t("invoicePage.goaPoints", { defaultValue: "Punkte" })}</th>
                                        <th className="goa-catalog-popup__num">{t("invoicePage.examValue")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groups.map((group) => (
                                        <Fragment key={group.section}>
                                            <tr className="goa-catalog-popup__section-row">
                                                <td colSpan={4}>
                                                    {group.section} — {SECTION_LABELS[group.section] ?? group.section}
                                                </td>
                                            </tr>
                                            {group.rows.map((c) => (
                                                <tr key={c.id}>
                                                    <td className="goa-catalog-popup__nr">{displayCode(c.nr)}</td>
                                                    <td>
                                                        {c.description}
                                                        {c.notes && <div className="goa-catalog-popup__note">{c.notes}</div>}
                                                    </td>
                                                    <td className="goa-catalog-popup__num">{c.points}</td>
                                                    <td className="goa-catalog-popup__num">{fmt(c.euroAmount)}</td>
                                                </tr>
                                            ))}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <p className="goa-catalog-popup__count">
                        {loading ? (
                            <span className="goa-catalog-popup__spinner" aria-hidden="true" />
                        ) : (
                            !error &&
                            filteredCodes.length > 0 &&
                            t("invoicePage.goaCatalogCount", { count: filteredCodes.length, defaultValue: "{{count}} Ziffern" })
                        )}
                    </p>
                </div>
            }
            actions={[{ label: t("popup.cancel"), variant: "default", onClick: onClose }]}
        />
    );
}
